import { useState, useCallback } from 'react';
import { useToast } from '@crisellevaldez/speculo-ui';
import { tenantConfigApi } from '../../../api/tenantConfig';

export interface UseClearCacheReturn {
  clearCache: (domain: string) => Promise<boolean>;
  isClearing: boolean;
}

/**
 * Hook to clear cached branding data for a tenant's domain
 */
export const useClearCache = (): UseClearCacheReturn => {
  const [isClearing, setIsClearing] = useState(false);
  const { showToast } = useToast();

  const clearCache = useCallback(
    async (domain: string): Promise<boolean> => {
      if (!domain) {
        showToast({
          message: 'No domain configured for this tenant',
          variant: 'error',
        });
        return false;
      }

      setIsClearing(true);
      try {
        const response = await tenantConfigApi.clearCache(domain);

        if (response.status !== 200) {
          throw new Error('Failed to clear cache');
        }

        showToast({
          message: 'Cache cleared successfully',
          variant: 'success',
        });
        return true;
      } catch (error: any) {
        console.error('Error clearing cache:', error);
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to clear cache';
        showToast({
          message: errorMessage,
          variant: 'error',
        });
        return false;
      } finally {
        setIsClearing(false);
      }
    },
    [showToast]
  );

  return {
    clearCache,
    isClearing,
  };
};
